import "./utility-tools.css";
import "./qr-code-scanner.css";
import {
  barcodeScanDimensions,
  decodedBarcodeSummary,
  validateBarcodeImageFile
} from "./barcode-scanner-core.js";

const root = document.querySelector("[data-barcode-scanner]");

if (root) {
  const ids = [
    "barcodeScannerStatus", "barcodeScannerWorkspace", "barcodeScannerInput", "barcodeScannerDropZone",
    "barcodeScannerChoose", "barcodeScannerCamera", "barcodeScannerCameraPanel", "barcodeScannerVideo",
    "barcodeScannerStopCamera", "barcodeScannerResult", "barcodeScannerResultKind", "barcodeScannerValue",
    "barcodeScannerOpen", "barcodeScannerCopy", "barcodeScannerAgain", "barcodeScannerCanvas"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const pending = new Map();
  const worker = new Worker("/dist/barcode-scanner-worker.js", { type: "module" });
  let requestId = 0;
  let cameraStream = null;
  let cameraFrame = 0;
  let lastCameraScan = 0;
  let cameraScanPending = false;

  function status(message = "", tone = "error") {
    elements.barcodeScannerStatus.textContent = message;
    elements.barcodeScannerStatus.hidden = !message;
    elements.barcodeScannerStatus.dataset.tone = tone;
  }

  function setBusy(busy, message = "") {
    root.setAttribute("aria-busy", String(busy));
    elements.barcodeScannerChoose.disabled = busy;
    elements.barcodeScannerCamera.disabled = busy;
    if (message) status(message, "progress");
  }

  function decodePixels(imageData) {
    const id = ++requestId;
    return new Promise((resolve, reject) => {
      pending.set(id, { resolve, reject });
      worker.postMessage({
        requestId: id,
        pixels: imageData.data.buffer,
        width: imageData.width,
        height: imageData.height
      }, [imageData.data.buffer]);
    });
  }

  worker.addEventListener("message", event => {
    const response = pending.get(event.data?.requestId);
    if (!response) return;
    pending.delete(event.data.requestId);
    if (event.data.error) response.reject(new Error(event.data.error));
    else response.resolve(event.data);
  });

  worker.addEventListener("error", () => {
    for (const response of pending.values())
      response.reject(new Error("The barcode scanner stopped unexpectedly."));
    pending.clear();
  });

  function drawToScanCanvas(source, sourceWidth, sourceHeight) {
    const dimensions = barcodeScanDimensions(sourceWidth, sourceHeight);
    const canvas = elements.barcodeScannerCanvas;
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return context.getImageData(0, 0, canvas.width, canvas.height);
  }

  async function imageSource(file) {
    if (typeof createImageBitmap === "function") {
      try {
        return await createImageBitmap(file, { imageOrientation: "from-image" });
      } catch {
        return createImageBitmap(file);
      }
    }
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      return image;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  function showResult(value, format) {
    stopCamera();
    const summary = decodedBarcodeSummary(value, format);
    elements.barcodeScannerResultKind.textContent = summary.kind;
    elements.barcodeScannerValue.value = value;
    elements.barcodeScannerOpen.hidden = !summary.url;
    if (summary.url) elements.barcodeScannerOpen.href = summary.url;
    else elements.barcodeScannerOpen.removeAttribute("href");
    elements.barcodeScannerWorkspace.hidden = true;
    elements.barcodeScannerCameraPanel.hidden = true;
    elements.barcodeScannerResult.hidden = false;
    status();
    window.toolboxAnalytics?.track("tool_complete");
    elements.barcodeScannerValue.focus();
  }

  async function scanFile(file) {
    if (!file) return;
    setBusy(true, "Preparing image…");
    let source;
    try {
      const header = new Uint8Array(await file.slice(0, 16).arrayBuffer());
      validateBarcodeImageFile(file, header);
      source = await imageSource(file);
      const width = source.width || source.naturalWidth;
      const height = source.height || source.naturalHeight;
      status("Looking for a barcode…", "progress");
      const result = await decodePixels(drawToScanCanvas(source, width, height));
      if (!result.value)
        throw new Error("No supported barcode was found. Try a sharper image with the complete bars visible.");
      showResult(result.value, result.format);
    } catch (error) {
      status(error?.message || "The barcode could not be read.");
    } finally {
      if (typeof source?.close === "function") source.close();
      elements.barcodeScannerInput.value = "";
      setBusy(false);
    }
  }

  function stopCamera() {
    if (cameraFrame) cancelAnimationFrame(cameraFrame);
    cameraFrame = 0;
    cameraScanPending = false;
    if (cameraStream) {
      for (const track of cameraStream.getTracks()) track.stop();
      cameraStream = null;
    }
    elements.barcodeScannerVideo.srcObject = null;
    elements.barcodeScannerCameraPanel.hidden = true;
    if (elements.barcodeScannerResult.hidden) elements.barcodeScannerWorkspace.hidden = false;
  }

  async function cameraLoop(timestamp) {
    if (!cameraStream) return;
    cameraFrame = requestAnimationFrame(cameraLoop);
    const video = elements.barcodeScannerVideo;
    if (cameraScanPending || timestamp - lastCameraScan < 220 || video.readyState < 2 || !video.videoWidth) return;
    cameraScanPending = true;
    lastCameraScan = timestamp;
    try {
      const result = await decodePixels(drawToScanCanvas(video, video.videoWidth, video.videoHeight));
      if (result.value) showResult(result.value, result.format);
    } catch (error) {
      status(error?.message || "The camera frame could not be scanned.");
      stopCamera();
    } finally {
      cameraScanPending = false;
    }
  }

  async function startCamera() {
    status();
    if (!navigator.mediaDevices?.getUserMedia) {
      status("Camera scanning is not available in this browser. Upload a barcode image instead.");
      return;
    }
    setBusy(true, "Requesting camera access…");
    window.toolboxAnalytics?.track("tool_start");
    try {
      cameraStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } }
      });
      elements.barcodeScannerVideo.srcObject = cameraStream;
      elements.barcodeScannerWorkspace.hidden = true;
      elements.barcodeScannerCameraPanel.hidden = false;
      await elements.barcodeScannerVideo.play();
      status("Hold the complete barcode steady inside the guide.", "progress");
      cameraFrame = requestAnimationFrame(cameraLoop);
    } catch (error) {
      stopCamera();
      status(error?.name === "NotAllowedError"
        ? "Camera access was not allowed. Allow access or upload a barcode image instead."
        : "The camera could not be started. Upload a barcode image instead.");
    } finally {
      setBusy(false);
    }
  }

  async function copyValue() {
    try {
      await navigator.clipboard.writeText(elements.barcodeScannerValue.value);
      status("Copied decoded value.", "success");
    } catch {
      elements.barcodeScannerValue.select();
      status("Select and copy the decoded value.", "progress");
    }
  }

  function reset() {
    stopCamera();
    elements.barcodeScannerResult.hidden = true;
    elements.barcodeScannerWorkspace.hidden = false;
    elements.barcodeScannerValue.value = "";
    status();
    elements.barcodeScannerChoose.focus();
  }

  elements.barcodeScannerChoose.addEventListener("click", () => elements.barcodeScannerInput.click());
  elements.barcodeScannerInput.addEventListener("change", () => scanFile(elements.barcodeScannerInput.files?.[0]));
  elements.barcodeScannerCamera.addEventListener("click", startCamera);
  elements.barcodeScannerStopCamera.addEventListener("click", stopCamera);
  elements.barcodeScannerCopy.addEventListener("click", copyValue);
  elements.barcodeScannerAgain.addEventListener("click", reset);
  elements.barcodeScannerDropZone.addEventListener("click", event => {
    if (event.target === elements.barcodeScannerDropZone) elements.barcodeScannerInput.click();
  });
  elements.barcodeScannerDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      elements.barcodeScannerInput.click();
    }
  });
  for (const type of ["dragenter", "dragover"]) {
    elements.barcodeScannerDropZone.addEventListener(type, event => {
      event.preventDefault();
      elements.barcodeScannerDropZone.classList.add("is-dragging");
    });
  }
  for (const type of ["dragleave", "drop"]) {
    elements.barcodeScannerDropZone.addEventListener(type, event => {
      event.preventDefault();
      elements.barcodeScannerDropZone.classList.remove("is-dragging");
    });
  }
  elements.barcodeScannerDropZone.addEventListener("drop", event => scanFile(event.dataTransfer?.files?.[0]));
  window.addEventListener("pagehide", () => {
    stopCamera();
    worker.terminate();
  }, { once: true });
}
